import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowDownIcon,
  ArrowPathIcon,
  ArrowUpIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { agentsAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';

const STAGE_ORDER = ['starter', 'discovery', 'pick', 'detail', 'trust', 'action'];
const STAGE_LABEL = {
  starter: 'Starter - first pills shown on greeting',
  discovery: 'Discovery — explore the catalogue',
  pick: 'Pick — narrow to one specific item',
  detail: 'Detail — price, specs, eligibility, schedule',
  trust: 'Trust — certifications, reviews, accreditation',
  action: 'Action — book, visit, enrol, callback',
};

function emptyPoolDoc() {
  return {
    version: 3,
    stages: STAGE_ORDER.reduce((acc, k) => ({ ...acc, [k]: [] }), {}),
  };
}

export default function PromptPoolEditorPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManagePromptPool = user?.role === 'superadmin' || user?.is_superuser;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [ragCollection, setRagCollection] = useState('');
  const [generatedAt, setGeneratedAt] = useState(null);
  const [pool, setPool] = useState(emptyPoolDoc());
  const [bulkInputs, setBulkInputs] = useState(
    STAGE_ORDER.reduce((acc, stage) => ({ ...acc, [stage]: '' }), {}),
  );

  useEffect(() => {
    if (!agentId) return;
    loadPool();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const loadPool = async () => {
    try {
      setLoading(true);
      const res = await agentsAPI.getPromptPool(agentId);
      const data = res.data || {};
      setAgentName(data.agent_name || '');
      setRagCollection(data.rag_collection || '');
      setGeneratedAt(data.generated_at || null);
      const incoming = data.pool;
      if (incoming && typeof incoming === 'object' && incoming.stages) {
        const stages = STAGE_ORDER.reduce((acc, k) => {
          const items = Array.isArray(incoming.stages[k]) ? incoming.stages[k] : [];
          acc[k] = items.map((it) =>
            typeof it === 'string'
              ? { question: it, evidence: '', source: 'manual' }
              : {
                  question: it.question || '',
                  evidence: it.evidence || '',
                  // Default unknown source to 'llm' (historical pools).
                  source: it.source || 'llm',
                },
          );
          return acc;
        }, {});
        setPool({ version: incoming.version || 3, stages });
      } else {
        setPool(emptyPoolDoc());
      }
    } catch (err) {
      console.error('Failed to load prompt pool:', err);
      toast.error(err?.response?.data?.detail || 'Failed to load prompt pool');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionChange = (stage, idx, value) => {
    setPool((prev) => {
      const next = { ...prev, stages: { ...prev.stages } };
      const items = [...(next.stages[stage] || [])];
      items[idx] = { ...items[idx], question: value };
      next.stages[stage] = items;
      return next;
    });
  };

  const handleEvidenceChange = (stage, idx, value) => {
    setPool((prev) => {
      const next = { ...prev, stages: { ...prev.stages } };
      const items = [...(next.stages[stage] || [])];
      items[idx] = { ...items[idx], evidence: value };
      next.stages[stage] = items;
      return next;
    });
  };

  const handleAddItem = (stage) => {
    setPool((prev) => {
      const next = { ...prev, stages: { ...prev.stages } };
      next.stages[stage] = [
        ...(next.stages[stage] || []),
        { question: '', evidence: '', source: 'manual' },
      ];
      return next;
    });
  };

  const handleMoveItem = (stage, idx, direction) => {
    setPool((prev) => {
      const next = { ...prev, stages: { ...prev.stages } };
      const items = [...(next.stages[stage] || [])];
      const targetIdx = idx + direction;
      if (targetIdx < 0 || targetIdx >= items.length) return prev;
      [items[idx], items[targetIdx]] = [items[targetIdx], items[idx]];
      next.stages[stage] = items;
      return next;
    });
  };

  const handleDeleteItem = (stage, idx) => {
    setPool((prev) => {
      const next = { ...prev, stages: { ...prev.stages } };
      next.stages[stage] = (next.stages[stage] || []).filter((_, i) => i !== idx);
      return next;
    });
  };

  const parseBulkPromptLine = (line) => {
    const cleanLine = line.replace(/^[-*]\s+/, '').replace(/^\d+[.)]\s+/, '').trim();
    if (!cleanLine) return [];

    if (cleanLine.includes('|')) {
      const [question, ...evidenceParts] = cleanLine.split('|');
      return [{
        question: (question || '').trim(),
        evidence: evidenceParts.join('|').trim(),
        source: 'manual',
      }].filter((it) => it.question);
    }

    const questions = cleanLine.match(/[^?]+[?]+/g);
    if (questions && questions.length > 1) {
      return questions.map((question) => ({
        question: question.trim(),
        evidence: '',
        source: 'manual',
      }));
    }

    return [{ question: cleanLine, evidence: '', source: 'manual' }];
  };

  const parseBulkPrompts = (text) =>
    (text || '')
      .split(/\r?\n/)
      .flatMap((line) => parseBulkPromptLine(line.trim()))
      .filter((it) => it.question);

  const handleBulkChange = (stage, value) => {
    setBulkInputs((prev) => ({ ...prev, [stage]: value }));
  };

  const handleBulkAdd = (stage) => {
    const itemsToAdd = parseBulkPrompts(bulkInputs[stage]);
    if (!itemsToAdd.length) {
      toast.error('Paste one prompt per line before adding.');
      return;
    }

    const existing = pool.stages[stage] || [];
    const seen = new Set(
      existing
        .map((it) => (it.question || '').trim().toLowerCase())
        .filter(Boolean),
    );
    const uniqueNewItems = itemsToAdd.filter((it) => {
      const key = it.question.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (!uniqueNewItems.length) {
      toast.error('No new prompts added. The pasted prompts already exist in this category.');
      return;
    }

    setPool((prev) => {
      const next = { ...prev, stages: { ...prev.stages } };
      next.stages[stage] = [...(next.stages[stage] || []), ...uniqueNewItems];
      return next;
    });

    setBulkInputs((prev) => ({ ...prev, [stage]: '' }));
    const skippedCount = itemsToAdd.length - uniqueNewItems.length;
    toast.success(
      skippedCount
        ? `Added ${uniqueNewItems.length} prompt${uniqueNewItems.length === 1 ? '' : 's'} to ${stage}; skipped ${skippedCount} duplicate${skippedCount === 1 ? '' : 's'}.`
        : `Added ${uniqueNewItems.length} prompt${uniqueNewItems.length === 1 ? '' : 's'} to ${stage}.`,
    );
  };

  const handleSave = async () => {
    const stages = {};
    for (const k of STAGE_ORDER) {
      const items = (pool.stages[k] || [])
        .map((it) => ({
          question: (it.question || '').trim(),
          evidence: (it.evidence || '').trim(),
        }))
        .filter((it) => it.question);
      if (items.length) stages[k] = items;
    }
    const payload = { version: 3, stages };
    try {
      setSaving(true);
      const res = await agentsAPI.updatePromptPool(agentId, payload);
      const data = res.data || {};
      toast.success(
        `Saved ${(data.suggested_prompts || []).length} pills. Future regens will preserve these and append new KB pills around them.`,
      );
      await loadPool();
    } catch (err) {
      console.error('Failed to save prompt pool:', err);
      toast.error(err?.response?.data?.detail || 'Failed to save prompt pool');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      const res = await agentsAPI.regeneratePrompts(agentId);
      const total = (res.data?.suggested_prompts || []).length;
      toast.success(`Pool now has ${total} pills. Existing pills preserved; new KB pills appended.`);
      await loadPool();
    } catch (err) {
      console.error('Failed to regenerate:', err);
      toast.error(err?.response?.data?.detail || 'Regeneration failed');
    } finally {
      setRegenerating(false);
    }
  };

  const totalCount = useMemo(
    () => STAGE_ORDER.reduce((sum, k) => sum + (pool.stages[k]?.length || 0), 0),
    [pool],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-sm text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 mb-2"
          >
            <ArrowLeftIcon className="w-4 h-4" /> Back to agents
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Prompt Pool — {agentName || agentId}</h1>
          <p className="text-sm text-gray-500">
            Collection: <span className="font-mono">{ragCollection || '—'}</span>
            {generatedAt && (
              <>
                {' · '}Generated: {new Date(generatedAt).toLocaleString()}
              </>
            )}
            {' · '}
            Total pills: {totalCount}
          </p>
        </div>
        <div className="flex gap-2">
          {canManagePromptPool && (
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={regenerating}
            className="rounded-md bg-fuchsia-600 px-4 py-2 text-sm font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50 inline-flex items-center gap-1"
            title="Regenerate prompts from the linked knowledge base"
          >
            <ArrowPathIcon className="w-4 h-4" />
            {regenerating ? 'Regenerating…' : 'Regenerate from KB'}
          </button>
          )}
          {canManagePromptPool && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <CheckIcon className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-800">
        <strong>Append-only pool.</strong> "Regenerate from KB" preserves every existing
        pill (manual or LLM) and only appends new KB-grounded questions whose text is not
        already present. Saving the pool tags every pill as manual; those will never be
        removed or modified by future regenerations.
      </div>

      {!canManagePromptPool && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
          Read-only view. Contact Serviti support to add, edit, delete, reorder, save, or regenerate prompt pills.
        </div>
      )}

      <div className="space-y-6">
        {STAGE_ORDER.map((stage) => (
          <div key={stage} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900">{STAGE_LABEL[stage]}</h2>
              <span className="text-xs text-gray-500">
                {pool.stages[stage]?.length || 0} pills
              </span>
            </div>
            <div className="mb-2 grid grid-cols-12 gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <div className="col-span-5">Prompt Pill Question</div>
              <div className="col-span-5">KB Evidence Quote</div>
              {canManagePromptPool && <div className="col-span-2 text-right">Actions</div>}
            </div>
            <p className="mb-3 text-xs text-gray-500">
              Questions are shown to visitors as clickable chips. Evidence is a short verbatim KB quote used for grounding review; it is not shown as the chat answer.
            </p>
            <div className="space-y-2">
              {(pool.stages[stage] || []).map((it, idx) => {
                const isManual = (it.source || 'llm') === 'manual';
                const itemsCount = pool.stages[stage]?.length || 0;
                return (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-5 flex items-center gap-2">
                    <span
                      className={`flex-shrink-0 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                        isManual
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                      title={isManual ? 'Manual — persisted across regenerations' : 'LLM-generated — replaceable by future regens if duplicate'}
                    >
                      {isManual ? 'manual' : 'llm'}
                    </span>
                    <input
                      type="text"
                      value={it.question}
                      onChange={(e) => handleQuestionChange(stage, idx, e.target.value)}
                      disabled={!canManagePromptPool}
                      placeholder="Question shown as a chip"
                      className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-50"
                    />
                  </div>
                  <input
                    type="text"
                    value={it.evidence}
                    onChange={(e) => handleEvidenceChange(stage, idx, e.target.value)}
                    disabled={!canManagePromptPool}
                    placeholder="Evidence — verbatim KB quote backing this pill"
                    className="col-span-5 rounded-md border border-gray-200 px-3 py-2 text-xs text-gray-600 focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-50"
                  />
                  {canManagePromptPool && (
                    <div className="col-span-2 flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => handleMoveItem(stage, idx, -1)}
                        disabled={idx === 0}
                        className="rounded border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-35 disabled:hover:bg-transparent"
                        title="Move up"
                      >
                        <ArrowUpIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveItem(stage, idx, 1)}
                        disabled={idx === itemsCount - 1}
                        className="rounded border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-35 disabled:hover:bg-transparent"
                        title="Move down"
                      >
                        <ArrowDownIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteItem(stage, idx)}
                        className="rounded border border-red-100 px-2 py-2 text-xs font-medium text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                );
              })}
              {canManagePromptPool && (
                <div className="space-y-3 pt-2">
                  <button
                    type="button"
                    onClick={() => handleAddItem(stage)}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    + Add pill to {stage}
                  </button>
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <label className="block text-xs font-medium text-gray-700">
                      Bulk add prompts
                    </label>
                    <textarea
                      value={bulkInputs[stage] || ''}
                      onChange={(e) => handleBulkChange(stage, e.target.value)}
                      rows={4}
                      placeholder="Paste one prompt per line. Optional: Question | Evidence quote"
                      className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500">
                        New prompts are appended to this category and duplicates are skipped.
                      </p>
                      <button
                        type="button"
                        onClick={() => handleBulkAdd(stage)}
                        className="rounded-md bg-white px-3 py-2 text-sm font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50"
                      >
                        Add pasted prompts
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
